import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, h, Method, Prop } from "@stencil/core"
import { HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { PaymentMethodEnum, PaymentMethodEnumsObj } from "../../enums/PaymentMethodEnum"
import { getClickToPayLegalLinks } from "../../helpers/ClickToPayLegalLinks"
import { srcScreenError } from "../../helpers/CommonRenders"
import { validateConsumerInfo } from "../../helpers/ConsumerInfoValidation"
import { resolvePrimaryActionState } from "../../helpers/PrimaryActionState"
import { getLanguageHandler, getLocale, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { ConsumerInfoFormConfig } from "../consumerInfoForm/ConsumerInfoFormConfig.model"
import { ConsumerInfoFormValidity } from "../consumerInfoForm/ConsumerInfoFormValidity.model"
import { ConsumerInfoValue, createEmptyConsumerInfoValue } from "../consumerInfoForm/ConsumerInfoValue.model"
import { InfoMessageModel } from "../infoMessage/InfoMessageModel"

let storeCardId = 0

/** Reusable controlled presentation for the network-branded Store Card step. */
@Component({
  tag: "store-card",
  styleUrl: "StoreCard.scss",
})
export class StoreCard {
  /** Merchant site preferred locale. */
  @Prop() locale?: string

  /** The single detected card network displayed by this step. */
  @Prop() paymentMethod: PaymentMethodEnum

  /** Current flow-owned Consumer draft. */
  @Prop() consumerInfoValue: ConsumerInfoValue = createEmptyConsumerInfoValue()

  /** Flow-owned Consumer field configuration. */
  @Prop() consumerInfoConfig?: ConsumerInfoFormConfig

  /** Flow-owned Consumer mapping errors. */
  @Prop() consumerInfoErrors: ConsumerInfoFormValidity["errors"] = []

  /** Whether all Consumer errors should be visible. */
  @Prop() showValidationErrors: boolean = false

  /** Whether the First-Time Remember Me control is displayed. */
  @Prop() showRememberMe: boolean = false

  /** Current flow-owned Remember Me choice. */
  @Prop() rememberMe?: boolean

  /** Whether the presentation and its actions are disabled. */
  @Prop() disabled: boolean = false

  /** Whether Store and Pay shows its processing state. */
  @Prop() isLoading: boolean = false

  /** Flow-level message displayed in the active step scroll area. */
  @Prop() infoMessage?: InfoMessageModel

  /** Callback receiving complete Consumer draft updates. */
  @Prop() consumerInfoOnChange?: (consumerInfo: ConsumerInfoValue) => void

  /** Callback receiving Consumer validity updates. */
  @Prop() consumerInfoValidityOnChange?: (validity: ConsumerInfoFormValidity) => void

  /** Callback receiving Remember Me changes. */
  @Prop() rememberMeOnChange?: (rememberMe: boolean) => void

  /** Guarded final action owned by the surrounding flow. */
  @Prop() storeAndPayOnClick?: () => void | Promise<void>

  /** Back action owned by the surrounding flow. */
  @Prop() goBackOnClick?: () => void

  private languageHandler: LanguageHandler
  private supportedLocale: string
  private errorMessage: string[] = []
  private readonly titleId = `store-card-title-${storeCardId++}`
  private consumerInfoForm?: HTMLConsumerInfoFormElement
  private scrollableContent?: HTMLDivElement

  componentWillLoad() {
    if (this.paymentMethod !== PaymentMethodEnumsObj.MC && this.paymentMethod !== PaymentMethodEnumsObj.VISA) {
      this.errorMessage.push("'paymentMethod' prop must identify one supported card network")
    }

    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.supportedLocale = getLocale(localeData)
  }

  private getNetworkText(key: string, network: string): string {
    return (this.languageHandler.getString(key) || "").replace("{network}", network)
  }

  /** Displays common validation errors and reports whether the Consumer draft is valid. */
  @Method()
  async reportConsumerValidity(): Promise<boolean> {
    return (await this.consumerInfoForm?.reportCommonValidity()) === true
  }

  /** Focuses the first invalid Consumer field. */
  @Method()
  async focusFirstConsumerError(): Promise<boolean> {
    return (await this.consumerInfoForm?.scrollToFirstError()) === true
  }

  /** Resets the Store Card content below its fixed header. */
  @Method()
  async scrollToTop(): Promise<boolean> {
    if (!this.scrollableContent) return false

    this.scrollableContent.scrollTop = 0
    this.scrollableContent.scrollLeft = 0
    return true
  }

  private renderLegalNotice() {
    const links = getClickToPayLegalLinks(this.paymentMethod, this.supportedLocale)[0]
    if (!links) {
      return null
    }

    const terms = this.languageHandler.getString("terms")
    const privacyNotice = this.languageHandler.getString("privacyNotice")
    const opensInNewWindow = this.languageHandler.getString("opensInNewWindow")

    return (
      <p class="storeCardLegalNotice">
        {this.getNetworkText("storeCardLegalBeforeTerms", links.network)}
        <a
          href={links.termsHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${links.network} ${terms}. ${opensInNewWindow}`}>
          {terms}
        </a>
        {this.getNetworkText("storeCardLegalBetweenLinks", links.network)}
        <a
          href={links.privacyNoticeHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${links.network} ${privacyNotice}. ${opensInNewWindow}`}>
          {privacyNotice}
        </a>
        {this.languageHandler.getString("storeCardLegalAfterPrivacy")}
      </p>
    )
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("Store Card", this.errorMessage)
    }

    const controlsDisabled = this.disabled || this.isLoading
    const isConsumerInfoValid = validateConsumerInfo(
      this.consumerInfoValue,
      this.consumerInfoConfig,
      this.consumerInfoErrors
    ).valid
    const primaryActionState = resolvePrimaryActionState({
      isLocallyValid: isConsumerInfoValid,
      isBusy: controlsDisabled,
      isProcessing: this.isLoading,
    })

    return (
      <section class="storeCard" aria-labelledby={this.titleId}>
        <header class="storeCardHeader">
          <h2 id={this.titleId} class="storeCardTitle">
            {this.languageHandler.getString("storeCard")}
          </h2>
          <p class="storeCardDescription">{this.languageHandler.getString("storeCardDescription")}</p>
          <div class="storeCardMark">
            <horizontal-mark
              locale={this.supportedLocale}
              paymentMethod={this.paymentMethod}
              horizontalMarkSize={HorizontalMarkSizeEnumsObj.M28}
            />
          </div>
        </header>

        <div ref={element => (this.scrollableContent = element)} class="storeCardScrollableContent">
          {this.infoMessage && Boolean(this.infoMessage.infoMessage) && (
            <info-message infoMessage={this.infoMessage} class="storeCardMessage" />
          )}
          <div class="storeCardConsumer">
            <consumer-info-form
              ref={element => (this.consumerInfoForm = element)}
              locale={this.supportedLocale}
              consumerInfoValue={this.consumerInfoValue}
              config={this.consumerInfoConfig}
              externalErrors={this.consumerInfoErrors}
              showValidationErrors={this.showValidationErrors}
              disabled={controlsDisabled}
              onConsumerInfoChange={event => {
                event.stopPropagation()
                this.consumerInfoOnChange?.(event.detail)
              }}
              onConsumerInfoValidityChange={event => {
                event.stopPropagation()
                this.consumerInfoValidityOnChange?.(event.detail)
              }}
              onConsumerInfoSubmit={event => {
                event.stopPropagation()
                if (!controlsDisabled) {
                  void this.storeAndPayOnClick?.()
                }
              }}
            />
          </div>

          <p class="storeCardContactNotice">{this.languageHandler.getString("captionVerification")}</p>

          {this.showRememberMe && (
            <div class="storeCardRememberMe">
              <remember-me-control
                locale={this.supportedLocale}
                checked={this.rememberMe}
                disabled={controlsDisabled}
                rememberMeOnChange={this.rememberMeOnChange}
              />
            </div>
          )}

          {this.renderLegalNotice()}

          <div class="storeCardActions">
            <button
              class={{
                storeCardPrimaryAction: true,
                primaryActionSoftDisabled: primaryActionState.softDisabled,
              }}
              type="button"
              disabled={primaryActionState.disabled}
              onClick={this.storeAndPayOnClick}>
              {primaryActionState.showSpinner ? (
                <loading-spinner loadingText={this.languageHandler.getString("processing")} />
              ) : (
                this.languageHandler.getString("storeAndPay")
              )}
            </button>
            <button
              class="storeCardBackAction"
              type="button"
              disabled={controlsDisabled}
              onClick={this.goBackOnClick}>
              {this.languageHandler.getString("goBack")}
            </button>
          </div>
        </div>
      </section>
    )
  }
}
